import { useState } from 'react';
import { UserPlus, ToggleLeft, ToggleRight, Trash2, Edit2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/axios';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { ROLE_LABELS } from '@/utils/permissions';
import { formatDate } from '@/utils/formatters';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import type { User, Role } from '@/types';

const ROLE_OPTIONS: { value: Role; label: string }[] = (Object.entries(ROLE_LABELS) as [Role, string][]).map(
  ([value, label]) => ({ value, label }),
);

export function AdminPage() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', role: 'sales_executive' as Role, city: '', department: '' });
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: { data: User[] } }>('/users?limit=100');
      return data.data.data;
    },
  });

  const createUser = useMutation({
    mutationFn: async (userData: typeof form) => {
      await apiClient.post('/users', userData);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowCreateModal(false);
      toast.success('User created successfully');
    },
  });

  const toggleAvailability = useMutation({
    mutationFn: async ({ id, isAvailable }: { id: string; isAvailable: boolean }) => {
      await apiClient.patch(`/users/${id}`, { isAvailable });
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const deleteUser = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/users/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User deleted');
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">User Management</h1>
          <p className="text-slate-500 text-sm mt-0.5">{data?.length ?? 0} users in the system</p>
        </div>
        <Button icon={<UserPlus size={14} />} onClick={() => setShowCreateModal(true)}>
          Create User
        </Button>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-card border border-slate-100 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16"><Spinner /></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                {['Name', 'Email / Phone', 'Role', 'City', 'Department', 'Availability', 'Last Login', 'Actions'].map(h => (
                  <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {data?.map(user => (
                <tr key={user.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-brand-navy/10 text-brand-navy flex items-center justify-center text-xs font-bold">
                        {user.name[0]}
                      </div>
                      <span className="font-medium text-slate-900">{user.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-slate-600">{user.email ?? '—'}</div>
                    <div className="text-xs text-slate-400">{user.phone ?? '—'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-brand-navy/10 text-brand-navy text-xs rounded-full font-medium">
                      {ROLE_LABELS[user.role as Role]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{user.city ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{(user as User & { department?: string }).department ?? '—'}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleAvailability.mutate({ id: user.id, isAvailable: !user.isAvailable })}
                      className={clsx(
                        'flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium transition-colors',
                        user.isAvailable
                          ? 'bg-green-50 text-green-700 hover:bg-green-100'
                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200',
                      )}
                    >
                      {user.isAvailable ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                      {user.isAvailable ? 'Available' : 'Unavailable'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{formatDate((user as User & { lastLoginAt?: string }).lastLoginAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button className="p-1.5 text-slate-400 hover:text-brand-cyan hover:bg-cyan-50 rounded-lg">
                        <Edit2 size={13} />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Delete user "${user.name}"?`)) deleteUser.mutate(user.id);
                        }}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create User Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New User"
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button
              onClick={() => createUser.mutate(form)}
              loading={createUser.isPending}
            >
              Create User
            </Button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <Input label="Full Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
          <Input label="Email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          <Input label="Phone" type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
          <Input label="Password" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required />
          <Select
            label="Role"
            options={ROLE_OPTIONS}
            value={form.role}
            onChange={e => setForm(f => ({ ...f, role: e.target.value as Role }))}
          />
          <Input label="City" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
          <div className="col-span-2">
            <Input label="Department" value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
